import React, { useState, useEffect } from "react";
import {
  useHistory,
  useParams,
  useRouteMatch,
} from "react-router-dom/cjs/react-router-dom";
import { message } from "antd";

import Header from "../../components/Header";
import { cnpj as mascaraCnpj } from "../../services/mask";
import { validaCnpj } from "../../services/validaCnpj";
import {
  cadastrarConcessionaria,
  alterarConcessionaria,
  detalheConcessionaria,
  listarGruposDealerAtivos,
} from "../../services/http/cadastrosService";

import "../Usuario/styles.css";
import "./styles.css";

const FORM_INICIAL = {
  codigoDealer: "",
  razaoSocial: "",
  nomeFantasia: "",
  cnpj: "",
  grupoDealerId: "",
  status: "Ativa",
};

/** Cadastro/edição/visualização de concessionária (RD 95133). */
export default function ConcessionariaNovoEditar() {
  const history = useHistory();
  const { id } = useParams();
  const somenteLeitura = !!useRouteMatch(
    "/cadastros/concessionarias/visualizar/:id"
  );

  const [form, setForm] = useState(FORM_INICIAL);
  const [grupos, setGrupos] = useState([]);
  const [erros, setErros] = useState({});
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    const carregarGrupos = async () => {
      const resposta = await listarGruposDealerAtivos();
      if (!resposta.fail) setGrupos(resposta.data ?? []);
    };

    carregarGrupos();
  }, []);

  useEffect(() => {
    if (!id) return;

    const carregar = async () => {
      const resposta = await detalheConcessionaria(id);

      if (resposta.fail) {
        message.error(
          resposta.errors?.message || "Não foi possível carregar a concessionária"
        );
        return;
      }

      const dados = resposta.data;
      setForm({
        codigoDealer: dados.codigoDealer ?? "",
        razaoSocial: dados.razaoSocial ?? "",
        nomeFantasia: dados.nomeFantasia ?? "",
        cnpj: dados.cnpj ?? "",
        grupoDealerId: dados.grupoDealerId ?? "",
        status: dados.status ?? "Ativa",
      });

      // Um grupo desativado depois do cadastro continua vinculado a esta
      // concessionária; sem isso ele sumiria do dropdown ao editar.
      if (dados.grupoDealerId && dados.grupoDealer) {
        setGrupos((anteriores) =>
          anteriores.some((grupo) => grupo.id === dados.grupoDealerId)
            ? anteriores
            : [
                ...anteriores,
                { id: dados.grupoDealerId, nome: `${dados.grupoDealer} (inativo)` },
              ]
        );
      }
    };

    carregar();
  }, [id]);

  const alterarCampo = (evento) => {
    const { name, value } = evento.target;

    setForm((anterior) => ({
      ...anterior,
      [name]: name === "cnpj" ? value.replace(/\D/g, "") : value,
    }));

    setErros((anterior) => ({ ...anterior, [name]: undefined }));
  };

  const validar = () => {
    const novosErros = {};

    if (!form.codigoDealer.trim())
      novosErros.codigoDealer = "Código Dealer é obrigatório";

    if (!form.razaoSocial.trim())
      novosErros.razaoSocial = "Razão Social é obrigatória";

    if (!form.nomeFantasia.trim())
      novosErros.nomeFantasia = "Nome Fantasia é obrigatório";

    if (!form.cnpj) novosErros.cnpj = "CNPJ é obrigatório";
    else if (form.cnpj.length !== 14 || !validaCnpj(form.cnpj))
      novosErros.cnpj = "CNPJ inválido";

    setErros(novosErros);
    return Object.keys(novosErros).length === 0;
  };

  const enviar = async (evento) => {
    evento.preventDefault();

    if (!validar()) return;

    setSalvando(true);

    try {
      const payload = {
        codigoDealer: form.codigoDealer.trim(),
        razaoSocial: form.razaoSocial.trim(),
        nomeFantasia: form.nomeFantasia.trim(),
        cnpj: form.cnpj,
        grupoDealerId: form.grupoDealerId || null,
        status: form.status,
      };

      const resposta = id
        ? await alterarConcessionaria(id, payload)
        : await cadastrarConcessionaria(payload);

      if (resposta.fail) {
        const mensagem =
          resposta.errors?.message || "Não foi possível salvar a concessionária";

        // O backend valida duplicidade de Código Dealer e CNPJ; mostra o erro no campo.
        if (mensagem.toLowerCase().includes("código dealer"))
          setErros((anterior) => ({ ...anterior, codigoDealer: mensagem }));
        else if (mensagem.toLowerCase().includes("cnpj"))
          setErros((anterior) => ({ ...anterior, cnpj: mensagem }));

        message.error(mensagem);
        return;
      }

      message.success(
        id ? "Concessionária alterada com sucesso" : "Concessionária cadastrada com sucesso"
      );
      history.push("/cadastros/concessionarias");
    } finally {
      setSalvando(false);
    }
  };

  const titulo = somenteLeitura
    ? "Visualizar Concessionária"
    : id
    ? "Editar Concessionária"
    : "Nova Concessionária";

  return (
    <>
      <Header style={{ marginBottom: "2rem" }} mostrarPesquisar={false} />

      <div className="container">
        <h1 className="title">{titulo}</h1>

        <form onSubmit={enviar} className="admin-form">
          <div className="form-section">
            <h2>Dados da Concessionária</h2>

            <div className="row">
              <div className="col-md-6 col-lg-3 mb-3">
                <div className="form-group">
                  <label>
                    Código Dealer <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    name="codigoDealer"
                    value={form.codigoDealer}
                    onChange={alterarCampo}
                    disabled={somenteLeitura}
                    maxLength={50}
                    className={`form-control ${
                      erros.codigoDealer ? "is-invalid" : ""
                    }`}
                  />
                  {erros.codigoDealer && (
                    <div className="invalid-feedback">{erros.codigoDealer}</div>
                  )}
                </div>
              </div>

              <div className="col-md-6 col-lg-5 mb-3">
                <div className="form-group">
                  <label>
                    Razão Social <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    name="razaoSocial"
                    value={form.razaoSocial}
                    onChange={alterarCampo}
                    disabled={somenteLeitura}
                    maxLength={200}
                    className={`form-control ${
                      erros.razaoSocial ? "is-invalid" : ""
                    }`}
                  />
                  {erros.razaoSocial && (
                    <div className="invalid-feedback">{erros.razaoSocial}</div>
                  )}
                </div>
              </div>

              <div className="col-md-6 col-lg-4 mb-3">
                <div className="form-group">
                  <label>
                    Nome Fantasia <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    name="nomeFantasia"
                    value={form.nomeFantasia}
                    onChange={alterarCampo}
                    disabled={somenteLeitura}
                    maxLength={200}
                    className={`form-control ${
                      erros.nomeFantasia ? "is-invalid" : ""
                    }`}
                  />
                  {erros.nomeFantasia && (
                    <div className="invalid-feedback">{erros.nomeFantasia}</div>
                  )}
                </div>
              </div>

              <div className="col-md-6 col-lg-4 mb-3">
                <div className="form-group">
                  <label>
                    CNPJ <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    name="cnpj"
                    value={mascaraCnpj(form.cnpj)}
                    onChange={alterarCampo}
                    disabled={somenteLeitura}
                    maxLength={18}
                    className={`form-control ${erros.cnpj ? "is-invalid" : ""}`}
                  />
                  {erros.cnpj && (
                    <div className="invalid-feedback">{erros.cnpj}</div>
                  )}
                </div>
              </div>

              <div className="col-md-6 col-lg-4 mb-3">
                <div className="form-group">
                  <label>Grupo Dealer</label>
                  <select
                    name="grupoDealerId"
                    value={form.grupoDealerId}
                    onChange={alterarCampo}
                    disabled={somenteLeitura}
                    className="form-control"
                  >
                    <option value="">Selecione o grupo</option>
                    {grupos.map((grupo) => (
                      <option key={grupo.id} value={grupo.id}>
                        {grupo.nome}
                      </option>
                    ))}
                  </select>
                  <small className="form-text text-muted">
                    Apenas grupos ativos podem ser selecionados.
                  </small>
                </div>
              </div>

              <div className="col-md-6 col-lg-4 mb-3">
                <div className="form-group">
                  <label>Status</label>
                  <select
                    name="status"
                    value={form.status}
                    onChange={alterarCampo}
                    disabled={somenteLeitura}
                    className="form-control"
                  >
                    <option value="Ativa">Ativa</option>
                    <option value="Inativa">Inativa</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="cancel-button"
              onClick={() => history.push("/cadastros/concessionarias")}
            >
              {somenteLeitura ? "Voltar" : "Cancelar"}
            </button>

            {!somenteLeitura && (
              <button type="submit" className="save-button" disabled={salvando}>
                {salvando ? "Salvando..." : "Salvar"}
              </button>
            )}
          </div>
        </form>
      </div>
    </>
  );
}
